import { useState, useMemo, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { SelectChevron } from '@/components/ui/Select';
import { useTimezone } from '@/contexts/TimezoneContext';
import { shouldYieldEscape } from '@/hooks/useEscapeLayer';
import {
  useShopFunnel,
  useProductEventSummary,
  useRecentProductEvents,
  useUserEventTimeline,
  type EventCategory,
  type RegistryEntry,
  type UserTimelineEvent,
} from '@/hooks/useProductEvents';

const PERIODS = [7, 30, 90] as const;
const PROP_KEYS = ['productId', 'kind', 'optionId', 'reason', 'cartValueNet'] as const;

const CATEGORIES: EventCategory[] = ['shop', 'upsell', 'gating', 'checkout', 'provisioning', 'other'];
const CATEGORY_LABELS: Record<EventCategory, string> = {
  shop: 'Shop',
  upsell: 'Upsell',
  gating: 'Gating',
  checkout: 'Checkout',
  provisioning: 'Provisioning',
  other: 'Other',
};

type EventsTab = 'overview' | 'funnel' | 'feed';
const TABS: { id: EventsTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'funnel', label: 'Shop funnel' },
  { id: 'feed', label: 'Feed' },
];

type LabelFn = (event: string) => string;

const CategoryBadge = ({ category }: { category: EventCategory }) => (
  <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
    {CATEGORY_LABELS[category] ?? category}
  </span>
);

const FilterSelect = ({
  value,
  onChange,
  ariaLabel,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  children: ReactNode;
}) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      className="appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
    >
      {children}
    </select>
    <SelectChevron className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
  </div>
);

const pct = (v: number | null): string => (v === null ? '-' : `${Math.round(v * 100)}%`);

/** Short key:value summary of known props; values are rendered as plain text. */
const summarizeProps = (props: Record<string, unknown>): string =>
  PROP_KEYS.filter((k) => props[k] !== undefined && props[k] !== null)
    .map((k) => `${k}: ${String(props[k])}`)
    .join(' · ');

const cardClass = 'bg-white rounded-2xl border border-gray-200 p-5';

const SourceBadge = ({ source }: { source: 'CLIENT' | 'SERVER' }) => (
  <span
    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
      source === 'SERVER' ? 'bg-primary-50 text-primary-800' : 'bg-gray-100 text-gray-700'
    }`}
  >
    {source === 'SERVER' ? 'Server' : 'Client'}
  </span>
);

const Timeline = ({
  userId,
  label: who,
  eventLabel: label,
  onClose,
}: {
  userId: number;
  label: string;
  eventLabel: LabelFn;
  onClose: () => void;
}) => {
  const { formatDateTime } = useTimezone();
  const { data, isLoading, error } = useUserEventTimeline(userId);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      const target = ev.target;
      if (
        shouldYieldEscape(ev) ||
        (target instanceof HTMLElement && (target.tagName === 'SELECT' || target.getAttribute('role') === 'listbox'))
      ) {
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <aside
      className={`${cardClass} min-w-0 space-y-3`}
      aria-label={`Event timeline for ${who}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">User timeline</h2>
          <p className="text-sm text-gray-600 break-all">{who}</p>
          <Link
            to={`/admin/users/${userId}`}
            className="text-sm text-primary-600 hover:underline"
          >
            Open user details
          </Link>
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:border-gray-400"
        >
          Close
        </button>
      </div>
      <p className="text-xs text-gray-500">Last 90 days, up to 200 events.</p>
      {isLoading && <p className="text-sm text-gray-500">Loading...</p>}
      {error && <p className="text-sm text-red-600">Could not load the timeline.</p>}
      {data && data.length === 0 && <p className="text-sm text-gray-500">No events.</p>}
      {data && data.length > 0 && (
        <ul className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
          {data.map((e: UserTimelineEvent) => (
            <li key={e.id} className="py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900">{label(e.event)}</span>
                <SourceBadge source={e.source} />
              </div>
              <div className="text-xs text-gray-500">{formatDateTime(e.createdAt)}</div>
              {summarizeProps(e.props) && (
                <div className="text-xs text-gray-600">{summarizeProps(e.props)}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

export const Events = () => {
  const { formatDateTime } = useTimezone();
  const [days, setDays] = useState<number>(30);
  const [tab, setTab] = useState<EventsTab>('overview');
  const [eventFilter, setEventFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<{ id: number; label: string } | null>(null);

  const feedHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const pendingFocusUserId = useRef<number | null>(null);
  const closeTimeline = useCallback(() => {
    setSelectedUser((cur) => {
      pendingFocusUserId.current = cur?.id ?? null;
      return null;
    });
  }, []);

  const summary = useProductEventSummary(days);
  const funnel = useShopFunnel(days, tab === 'funnel');
  const recent = useRecentProductEvents(days, eventFilter, categoryFilter, selectedUser?.id ?? null, tab === 'feed');

  const registry: RegistryEntry[] = useMemo(() => summary.data?.registry ?? [], [summary.data]);
  const labels = useMemo(() => new Map(registry.map((r) => [r.event, r.label])), [registry]);
  const label: LabelFn = (event) => labels.get(event) ?? event;
  const eventOptions = registry.filter((r) => !categoryFilter || r.category === categoryFilter);

  const overviewEmpty = !!summary.data && summary.data.events.every((e) => e.total === 0);
  const funnelEmpty =
    !!funnel.data &&
    funnel.data.cohort.every((c) => c.users === 0) &&
    funnel.data.stepCounts.every((c) => c.total === 0) &&
    funnel.data.failures.total === 0;
  const active = tab === 'overview' ? summary : tab === 'funnel' ? funnel : recent;
  const isLoading = active.isLoading;
  const hasError = !!active.error;
  const isEmpty = tab === 'overview' ? overviewEmpty : tab === 'funnel' ? funnelEmpty : false;

  // Return focus to the user button that opened the timeline (re-queried: the list may re-render).
  // Gives up after ~1 s, or as soon as the user has focused something else.
  useEffect(() => {
    if (selectedUser !== null || pendingFocusUserId.current === null) return;
    const id = pendingFocusUserId.current;
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const active = document.activeElement;
      const done = () => {
        clearInterval(interval);
        pendingFocusUserId.current = null;
      };
      if (active && active !== document.body) return done();
      const btn = document.querySelector<HTMLButtonElement>(`[data-user-btn="${id}"]`);
      if (btn) {
        btn.focus();
        return done();
      }
      if (Date.now() - startedAt > 1000) done();
    }, 100);
    return () => clearInterval(interval);
  }, [selectedUser]);

  const openFeedFor = (event: string) => {
    setEventFilter(event);
    setCategoryFilter('');
    setTab('feed');
  };

  const handleCategoryChange = (c: string) => {
    setCategoryFilter(c);
    const stillValid = registry.some((r) => r.event === eventFilter && (!c || r.category === c));
    if (!stillValid) setEventFilter('');
  };

  const firstUsers = funnel.data?.cohort[0]?.users ?? 0;
  const dailyMax = Math.max(1, ...(summary.data?.daily.map((d) => d.total) ?? [0]));
  const sortedEvents = [...(summary.data?.events ?? [])].sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Events"
        subtitle="First-party product tracking. It may be switched off on this server."
        documentTitle="Admin - Events"
        action={
          <div className="inline-flex flex-wrap gap-1 rounded-xl border border-gray-200 bg-white p-1" role="group" aria-label="Period">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                aria-pressed={days === p}
                className={`rounded-lg px-3 py-1 text-sm border border-transparent focus:outline-none focus-visible:border-primary-500 ${
                  days === p ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p} days
              </button>
            ))}
          </div>
        }
      />

      <div role="tablist" aria-label="Events sections" className="flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:bg-gray-100 focus-visible:border-primary-500 ${
              tab === t.id
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'feed' && isLoading && <p className="text-sm text-gray-500">Loading...</p>}

      {tab !== 'feed' && hasError && !isLoading && (
        <div className={`${cardClass} border-red-200`} role="alert">
          <p className="text-sm text-red-700">Could not load events.</p>
          <button
            type="button"
            onClick={() => {
              void active.refetch();
            }}
            className="mt-2 rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:border-gray-400"
          >
            Try again
          </button>
        </div>
      )}

      {isEmpty && (
        <div className={cardClass}>
          <p className="text-sm text-gray-600">
            No events yet. Tracking may be switched off (PRODUCT_EVENTS_ENABLED).
          </p>
        </div>
      )}

      {tab === 'overview' && summary.data && !isEmpty && (
        <div className="space-y-6">
          <section className={`${cardClass} min-w-0 space-y-3`} aria-label="Daily events">
            <h2 className="text-base font-semibold text-gray-900">Events per day</h2>
            <div className="flex h-32 items-end gap-0.5" role="img" aria-label="Daily total events bar chart">
              {summary.data.daily.map((d) => (
                <div
                  key={d.date}
                  className="flex-1 min-w-[2px] rounded-t bg-primary-500"
                  style={{ height: `${Math.max(2, (d.total / dailyMax) * 100)}%` }}
                  title={`${d.date}: ${d.total}`}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500">Days in UTC</p>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{summary.data.daily[0]?.date ?? ''}</span>
              <span>Peak: {dailyMax === 1 && summary.data.daily.every((d) => d.total === 0) ? 0 : dailyMax}</span>
              <span>{summary.data.daily[summary.data.daily.length - 1]?.date ?? ''}</span>
            </div>
          </section>

          <section className={`${cardClass} min-w-0 space-y-3`} aria-label="All events">
            <h2 className="text-base font-semibold text-gray-900">All events</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-2 pr-3 font-medium">Event</th>
                    <th className="py-2 pr-3 font-medium">Category</th>
                    <th className="py-2 pr-3 font-medium text-right">Total</th>
                    <th className="py-2 font-medium text-right">Unique users</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEvents.map((e) => (
                    <tr key={e.event} className="border-t border-gray-100">
                      <td className="py-2 pr-3">
                        <button
                          type="button"
                          onClick={() => openFeedFor(e.event)}
                          className="text-left text-primary-600 hover:underline focus:outline-none focus:underline"
                        >
                          {e.label || e.event}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        <CategoryBadge category={e.category} />
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{e.total}</td>
                      <td className="py-2 text-right tabular-nums">{e.uniqueUsers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {tab === 'funnel' && funnel.data && !isEmpty && (
        <div className="grid gap-6 lg:grid-cols-3">
          <section className={`${cardClass} lg:col-span-2 min-w-0 space-y-4`} aria-label="Funnel">
            <h2 className="text-base font-semibold text-gray-900">Funnel (unique users)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-2 pr-3 font-medium">Step</th>
                    <th className="py-2 pr-3 font-medium text-right">Users</th>
                    <th className="py-2 pr-3 font-medium text-right">From previous</th>
                    <th className="py-2 pr-3 font-medium text-right">From first</th>
                    <th className="py-2 w-1/3 font-medium">
                      <span className="sr-only">Share of first step</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {funnel.data.cohort.map((step) => {
                    const width = firstUsers > 0 ? Math.min(100, (step.users / firstUsers) * 100) : 0;
                    return (
                      <tr key={step.event} className="border-t border-gray-100">
                        <td className="py-2 pr-3 text-gray-900">{label(step.event)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{step.users}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{pct(step.conversionFromPrevious)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{pct(step.conversionFromFirst)}</td>
                        <td className="py-2">
                          <div className="h-2 w-full rounded-full bg-gray-100" aria-hidden="true">
                            <div className="h-2 rounded-full bg-primary-500" style={{ width: `${width}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 mb-1">Raw totals (events / unique users)</h3>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                {funnel.data.stepCounts.map((s) => (
                  <li key={s.event}>
                    {label(s.event)}: {s.total} / {s.uniqueUsers}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className={`${cardClass} min-w-0 space-y-3`} aria-label="Failures">
            <h2 className="text-base font-semibold text-gray-900">Failures</h2>
            <p className="text-2xl font-semibold text-gray-900 tabular-nums">{funnel.data.failures.total}</p>
            {funnel.data.failures.byReason.length > 0 && (
              <ul className="space-y-1 text-sm text-gray-700">
                {funnel.data.failures.byReason.map((r) => (
                  <li key={r.reason} className="flex justify-between gap-2">
                    <span className="break-all">{r.reason}</span>
                    <span className="tabular-nums">{r.total}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500">
              Replaced or cancelled checkouts (not failures): {funnel.data.nonFailureVoids}
            </p>
          </section>
        </div>
      )}

      {tab === 'feed' && (
        <div className={`grid gap-6 ${selectedUser ? 'xl:grid-cols-3' : ''}`}>
          <section className={`${cardClass} min-w-0 space-y-3 ${selectedUser ? 'xl:col-span-2' : ''}`} aria-label="Recent activity">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 ref={feedHeadingRef} tabIndex={-1} className="text-base font-semibold text-gray-900 focus:outline-none">Recent activity</h2>
              <div className="flex flex-wrap items-center gap-2">
                <FilterSelect value={categoryFilter} onChange={handleCategoryChange} ariaLabel="Filter by category">
                  <option value="">All categories</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </FilterSelect>
                <FilterSelect value={eventFilter} onChange={setEventFilter} ariaLabel="Filter by event">
                  <option value="">All events</option>
                  {eventOptions.map((r) => (
                    <option key={r.event} value={r.event}>
                      {r.label || r.event}
                    </option>
                  ))}
                </FilterSelect>
                {selectedUser && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      feedHeadingRef.current?.focus();
                    }}
                    className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100 focus:outline-none focus:border-gray-400"
                    aria-label="Clear user filter"
                  >
                    User: {selectedUser.label} x
                  </button>
                )}
              </div>
            </div>
            {recent.isLoading && <p className="text-sm text-gray-500">Loading...</p>}
            {recent.error && !recent.isLoading && (
              <p className="text-sm text-red-600" role="alert">Could not load the feed.</p>
            )}
            {recent.data && recent.data.events.length === 0 && (
              <p className="text-sm text-gray-500">No events for these filters.</p>
            )}
            {recent.data && recent.data.events.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="py-2 pr-3 font-medium">Time</th>
                      <th className="py-2 pr-3 font-medium">User</th>
                      <th className="py-2 pr-3 font-medium">Brand</th>
                      <th className="py-2 pr-3 font-medium">Event</th>
                      <th className="py-2 pr-3 font-medium">Source</th>
                      <th className="py-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.data.events.map((e) => (
                      <tr key={e.id} className="border-t border-gray-100 align-top">
                        <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{formatDateTime(e.createdAt)}</td>
                        <td className="py-2 pr-3">
                          {e.user ? (
                            <button
                              type="button"
                              data-user-btn={e.user.id}
                              onClick={() => e.user && setSelectedUser({ id: e.user.id, label: e.user.email })}
                              className="text-left text-primary-600 hover:underline focus:outline-none focus:underline"
                            >
                              <span className="break-all">{e.user.email}</span>
                              {e.user.name && <span className="block text-xs text-gray-500">{e.user.name}</span>}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-gray-700">{e.team?.name ?? '-'}</td>
                        <td className="py-2 pr-3 text-gray-900">{label(e.event)}</td>
                        <td className="py-2 pr-3">
                          <SourceBadge source={e.source} />
                        </td>
                        <td className="py-2 text-xs text-gray-600">{summarizeProps(e.props)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {recent.data && recent.data.events.length === 100 && (
              <p className="text-xs text-gray-500">Showing the latest 100 events. Narrow with the filters.</p>
            )}
          </section>
          {selectedUser && (
            <Timeline
              userId={selectedUser.id}
              label={selectedUser.label}
              eventLabel={label}
              onClose={closeTimeline}
            />
          )}
        </div>
      )}
    </div>
  );
};
